import React, { useContext } from 'react';
import PageLayout from '../pagelayout';
import { useNavigate, useParams } from 'react-router-dom';
import { StudiestedApiContext } from '../api/StudiestedApiContext';
import { useLoader } from '../api/useLoader';
import { useQuery } from '../api/useQuery';
import Button from '../components/button';
import PanelHeader from '../components/panelheader/panelHeader';
import {
  FSAktivitetstatustypeDto,
  PraksisaktivitetStatusDto,
} from '../api/generated/studiested-generated-api';
import translation from './activity.uha.i18n';
import commonStatusTranslations from '../commontranslations/uhaStatuses.i18n';
import ActivityInfo from './activityInfo.uha';
import ActivityTabsRoutes from './activityTabs.uha';
import { NotificationFunctionContext } from '../components/notifications/notificationProvider';
import {
  ActivityContextUHA,
  ActivityContextUHAType,
  useActivityContextUHA,
} from './activitycontexts';
import { useFormatMessageFor } from '../i18n/translation';
import useAddErrorNotificationCallback from '../hooks/useAddErrorNotificationCallback';
import Colors from '../alpColors';
import * as css from './activity.uha.module.scss';
import classNames from 'classnames';
import { dateTimeNorwegianLocale } from '../date.utils';

export const INGEN_AKTIVITETSKRAV = {
  id: '0',
  navn: 'Ingen aktivitetskrav',
};

const ActivityWrapper = () => {
  const { activityId } = useParams<{ activityId: string }>();
  const { praksiskalenderApi } = useContext(StudiestedApiContext);
  const { data: praksisaktivitet, reload: reloadPraksisaktivitet } = useLoader(
    () =>
      praksiskalenderApi.praksisaktivitetIdGet({
        pathParams: { id: activityId! },
      }),
    [activityId],
    false,
    true,
  );

  const labels = useFormatMessageFor(translation);

  return praksisaktivitet ? (
    <Activity
      praksisaktivitet={praksisaktivitet}
      reloadPraksisaktivitet={reloadPraksisaktivitet}
    />
  ) : (
    <PageLayout
      documentTitle={labels.documentTitle}
      isIndented
      title={labels.loading}
      subtitle={labels.loading}
    />
  );
};

const Activity = (value: ActivityContextUHAType) => (
  <ActivityContextUHA.Provider value={value}>
    <ActivityContent />
  </ActivityContextUHA.Provider>
);

const useStatusMessage = (): string => {
  const { praksisaktivitet } = useActivityContextUHA();
  const labels = useFormatMessageFor(translation);
  const commonStatusLabels = useFormatMessageFor(commonStatusTranslations);

  switch (praksisaktivitet.status) {
    case PraksisaktivitetStatusDto.ManglerBehov:
      return labels.manglerBehov;
    case PraksisaktivitetStatusDto.KanDeles:
      return commonStatusLabels.kanDeles;
    case PraksisaktivitetStatusDto.DeltMenManglerKapasitet:
      return commonStatusLabels.deltMenManglerKapasitet;
    case PraksisaktivitetStatusDto.DeltMedNoenPraksissteder:
      return labels.deltMedNoenPraksissteder;
    case PraksisaktivitetStatusDto.DeltOgHarKapasitet:
      return commonStatusLabels.deltOgHarKapasitet;
    case PraksisaktivitetStatusDto.VenterPaaOnskeliste:
      return `${labels.venterPaaOnskeliste} ${praksisaktivitet.onskeliste.kanEndresTil}`;
    case PraksisaktivitetStatusDto.AvsluttetOnskeliste:
      return labels.avsluttetOnskeliste;
    case PraksisaktivitetStatusDto.StudenterManglerKlasse:
      return commonStatusLabels.studenterManglerKlasse;
    case PraksisaktivitetStatusDto.StudenterManglerPlass:
      return commonStatusLabels.studenterManglerPlass;
    case PraksisaktivitetStatusDto.AlleStudenterFordelt:
      return commonStatusLabels.alleStudenterFordelt;
    case PraksisaktivitetStatusDto.FordelingsresultatDelt:
      return commonStatusLabels.fordelingsresultatDelt;
    case PraksisaktivitetStatusDto.Gjennomfoert:
      return commonStatusLabels.gjennomfoert;
    case PraksisaktivitetStatusDto.Aktiv:
      return commonStatusLabels.aktiv;
    case PraksisaktivitetStatusDto.Avtalefeil:
      return commonStatusLabels.avtalefeil;
    case undefined:
      return labels.praksisaktivitet;
  }
};

const useFsStatusMessagePostfix = (
  statustype: FSAktivitetstatustypeDto,
): string => {
  const labels = useFormatMessageFor(translation);

  switch (statustype) {
    case FSAktivitetstatustypeDto.IngenFeil:
      return labels.fsStatusIngenFeil;
    case FSAktivitetstatustypeDto.FeilVedPublisering:
      return labels.fsStatusFeilVedPublisering;
    case FSAktivitetstatustypeDto.UkjentFeil:
      return labels.fsStatusUkjentFeil;
  }
};

const useFsStatusMessage = (): string => {
  const {
    praksisaktivitet: {
      fsStatus: { status },
    },
  } = useActivityContextUHA();
  const postfix = useFsStatusMessagePostfix(status);
  const { fsStatus } = useFormatMessageFor({
    fsStatus: { ...translation.fsStatus, values: { postfix } },
  });

  return fsStatus;
};

const useSistPublisertTilFsMessage = (): string => {
  const {
    praksisaktivitet: {
      fsStatus: { sistePubliseringTilFS },
    },
  } = useActivityContextUHA();
  const labels = useFormatMessageFor(translation);

  return `${labels.sistPublisertTilFS} ${
    sistePubliseringTilFS
      ? dateTimeNorwegianLocale(new Date(sistePubliseringTilFS))
      : labels.ikkePublisert
  }`;
};

const useFsStatusStyle = (): string => {
  const { praksisaktivitet } = useActivityContextUHA();

  return classNames(
    css.fsStatus,
    praksisaktivitet.fsStatus.status === FSAktivitetstatustypeDto.IngenFeil
      ? css.ok
      : css.error,
  );
};

const ActivityContent = () => {
  const labels = useFormatMessageFor(translation);
  const isCreating = !!useQuery().get('create');
  const { praksiskalenderApi } = useContext(StudiestedApiContext);
  const { addSuccessNotification } = useContext(NotificationFunctionContext);
  const errorNotificationCallback = useAddErrorNotificationCallback();
  const { praksisaktivitet } = useActivityContextUHA();
  const navigate = useNavigate();
  const statusMessage = useStatusMessage();
  const fsStatusMessage = useFsStatusMessage();
  const sistPublisertTilFsMessage = useSistPublisertTilFsMessage();
  const fsStatusStyle = useFsStatusStyle();

  const back = () => {
    if (isCreating) {
      deleteHandler(false, false);
    }
    onClose();
  };

  const onClose = () => {
    navigate('/calendar');
  };

  const deleteHandler = (
    showSnackbarMsg: boolean = true,
    closeOnSubmit: boolean = true,
  ): void => {
    praksiskalenderApi
      .praksisaktivitetIdDelete({ pathParams: { id: praksisaktivitet.id } })
      .then(() => {
        if (showSnackbarMsg) {
          addSuccessNotification(labels.deleteSuccess);
        }
        if (closeOnSubmit) onClose();
      })
      .catch(errorNotificationCallback);
  };

  const { emne } = praksisaktivitet;
  const title = `${emne.kode} - ${emne.navn}`;

  return (
    <PageLayout
      isIndented
      title={title}
      subtitle={labels.pageSubtitle}
      actionItems={
        <Button.Tertiary onClick={back}>{labels.back}</Button.Tertiary>
      }
    >
      <PanelHeader
        backgroundColor={Colors.fiord}
        color={Colors.white}
        title={statusMessage}
        customStatus={{
          statusTitle: fsStatusMessage,
          className: fsStatusStyle,
          tooltip: sistPublisertTilFsMessage,
        }}
        padLeft
      />
      <ActivityInfo />
      <ActivityTabsRoutes />
    </PageLayout>
  );
};

export default ActivityWrapper;
